import type { BlockedPerson, BlockFilter, BlocksPort, ListingView, PostView } from './ports.ts';
import type { WorkerClient } from './worker.ts';

type Raw = { uid?: unknown; name?: unknown; handle?: unknown; photoUrl?: unknown; blockedAt?: unknown };

const text = (value: unknown) => (typeof value === 'string' ? value : '');
export const NO_BLOCKS: BlockFilter = { users: new Set(), shops: new Set(), mine: new Set() };
const FILTER_TTL_MS = 60_000;

function person(raw: Raw): BlockedPerson | null {
  const uid = text(raw.uid);
  if (!uid) return null;
  const photo = text(raw.photoUrl);
  return { uid, name: text(raw.name) || 'Guildite member', handle: text(raw.handle) || null, photoUrl: photo.startsWith('https://') ? photo : null, blockedAt: text(raw.blockedAt) || null };
}

const people = (list: unknown) => (Array.isArray(list) ? list as Raw[] : []).map(person).filter((item): item is BlockedPerson => Boolean(item));

// What a list leaves out: posts by someone on either side of a block, and
// listings of shops that blocked you.
export const visiblePosts = (posts: PostView[], filter: BlockFilter) => posts.filter(post => !filter.users.has(post.authorId));
export const visibleListings = <T extends Pick<ListingView, 'shopId'>>(listings: T[], filter: BlockFilter) => listings.filter(listing => !filter.shops.has(listing.shopId));

export function createBlocks({ worker, currentUid }: { worker: WorkerClient; currentUid: () => string | null }): BlocksPort {
  // One request a minute at most; a block or unblock here clears it. If it
  // can't load, nothing is hidden (the Worker still refuses every action).
  let cached: { uid: string; at: number; filter: Promise<BlockFilter> } | null = null;
  const forget = () => { cached = null; };

  async function load(): Promise<BlockFilter> {
    const data = await worker.call<{ users?: unknown; shops?: unknown; mine?: unknown }>('get_block_filter');
    const list = (value: unknown) => new Set((Array.isArray(value) ? value : []).map(String));
    // An older Worker sends no mine: nothing is hidden in guild chat then.
    return { users: list(data?.users), shops: list(data?.shops), mine: list(data?.mine) };
  }

  return {
    async filter() {
      const uid = currentUid();
      if (!uid) return NO_BLOCKS;
      if (!cached || cached.uid !== uid || Date.now() - cached.at > FILTER_TTL_MS) {
        cached = { uid, at: Date.now(), filter: load().catch(() => { forget(); return NO_BLOCKS; }) };
      }
      return cached.filter;
    },
    blocked: async () => people((await worker.call<{ people?: unknown }>('list_blocked_users')).people),
    async block(uid) { await worker.call('block_user', { targetUid: uid }); forget(); },
    async unblock(uid) { await worker.call('unblock_user', { targetUid: uid }); forget(); },
    shopBlocked: async shopId => people((await worker.call<{ people?: unknown }>('list_shop_blocks', { shopId })).people),
    async blockForShop(shopId, uid) { await worker.call('block_shop_user', { shopId, targetUid: uid }); },
    async unblockForShop(shopId, uid) { await worker.call('unblock_shop_user', { shopId, targetUid: uid }); },
  };
}
